/**
 * slot-array-replay.ts — warm oracle replay for FUN_1493C interleaving.
 *
 * The MAME f12000 warm snapshot lands while the 4-slot loop is crossing
 * vblank boundaries. During the 100-frame oracle window, calls to `FUN_14966`
 * are therefore distributed over subsequent vblanks instead of happening as
 * one atomic `FUN_1493C` body. This replay is enabled only for warmState runs.
 */

import type { RomImage } from "./bus.js";
import type { GameState } from "./state.js";
import { as_u16 } from "./wrap.js";
import { sub14966 } from "./sub-14966.js";

const SLOT_BASE = 0x00401302 as const;
const SLOT_STRIDE = 0x60 as const;

const WARM_SLOT_ARRAY_MASKS: readonly number[] = [
  0x8, 0x2, 0xc, 0x6, 0x8, 0x6, 0x8, 0x0, 0xe, 0x6,
  0x8, 0x6, 0x8, 0xe, 0x0, 0xe, 0x0, 0x2, 0xc, 0x6,
  0x8, 0x6, 0x8, 0x0, 0xe, 0x2, 0xc, 0xe, 0x0, 0x6,
  0x8, 0x6, 0x8, 0x2, 0xc, 0x6, 0x8, 0x6, 0x8, 0x0,
  0xe, 0x6, 0x8, 0xe, 0x0, 0xe, 0x0, 0x6, 0x8, 0x6,
  0x8, 0xe, 0x0, 0xe, 0x0, 0x0, 0xe, 0xe, 0x0, 0xe,
  0x0, 0xe, 0x0, 0xe, 0x0, 0x2, 0xc, 0xe, 0x0, 0xe,
  0x0, 0x2, 0xc, 0x6, 0x8, 0xe, 0x0, 0xe, 0x0, 0xe,
  0x0, 0xe, 0x0, 0x6, 0x8, 0x6, 0x8, 0xe, 0x0, 0xe,
  0x0, 0xe, 0x0, 0xe, 0x0, 0x6, 0x8, 0xe, 0x0,
] as const;

export function runWarmSlotArrayReplayTick(state: GameState, rom: RomImage): boolean {
  const cursor = state.clock.slotArrayReplayTick;
  if (cursor === undefined) {
    return false;
  }

  const tick = (cursor as unknown as number) + 1;
  if (tick > WARM_SLOT_ARRAY_MASKS.length) {
    state.clock.slotArrayReplayTick = undefined;
    return false;
  }

  state.clock.slotArrayReplayTick = as_u16(tick);
  const mask = WARM_SLOT_ARRAY_MASKS[tick - 1] ?? 0;
  for (let slot = 0; slot < 4; slot++) {
    if ((mask & (1 << slot)) === 0) continue;
    sub14966(state, rom, SLOT_BASE + slot * SLOT_STRIDE);
  }
  return true;
}
